/* =============================================================
   Iberico Seal Verifier — single-IIFE renderer.
   Reads the seal code from the URL query param, calls the Supabase
   Edge Function, renders cards from cloned <template> nodes.

   Boots on both DOMContentLoaded (standalone) and shopify:section:load
   (so the Shopify theme customizer re-renders correctly when the
   merchant edits the section — single most common first-ship bug).
   ============================================================= */

(function () {
  'use strict';

  var SELECTOR = '[data-iberico-seal-verifier]';

  // ------------ utilities ------------

  function isHttpsUrl(s) {
    return typeof s === 'string' && /^https:\/\//i.test(s);
  }

  function setText(node, value) {
    if (!node) return;
    node.textContent = value == null ? '' : String(value);
  }

  function setImg(img, url, altText) {
    if (!img) return;
    if (altText != null) img.alt = String(altText);
    if (isHttpsUrl(url)) {
      img.style.display = '';
      img.src = url;
      img.onerror = function () { img.style.display = 'none'; };
    } else {
      img.style.display = 'none';
    }
  }

  function cloneTemplate(root, key) {
    var t = root.querySelector('template[data-tpl="' + key + '"]');
    if (!t || !t.content || !t.content.firstElementChild) return null;
    return t.content.firstElementChild.cloneNode(true);
  }

  function hasInfo(node) {
    return !!(node && node.informacion && node.informacion.titulo);
  }

  // ------------ state machine ------------

  function setState(root, state) {
    var states = root.querySelectorAll('[data-state]');
    for (var i = 0; i < states.length; i++) {
      states[i].hidden = states[i].getAttribute('data-state') !== state;
    }
    if (state === 'loading') {
      root.setAttribute('aria-busy', 'true');
    } else {
      root.removeAttribute('aria-busy');
    }
  }

  // ------------ renderers ------------

  function renderEmpty(root, cfg) {
    setText(root.querySelector('[data-empty-heading]'), cfg.headingEmpty);
    var form = root.querySelector('[data-empty-form]');
    if (form && !form.dataset.bound) {
      form.dataset.bound = '1';
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var input = root.querySelector('[data-empty-input]');
        var v = (input && input.value || '').trim();
        if (!v) return;
        var url = new URL(window.location.href);
        url.searchParams.set(cfg.queryParam, v);
        window.location.href = url.toString();
      });
    }
    setState(root, 'empty');
  }

  function renderError(root, title, subtitle, opts) {
    opts = opts || {};
    setText(root.querySelector('[data-error-title]'), title);
    setText(root.querySelector('[data-error-subtitle]'), subtitle);

    var retry = root.querySelector('[data-error-retry]');
    var back  = root.querySelector('[data-error-back]');
    if (retry) {
      retry.hidden = !opts.retry;
      if (opts.retry) {
        retry.onclick = function () { init(root); };
      }
    }
    if (back) {
      back.hidden = !opts.back;
    }
    setState(root, 'error');
  }

  function buildHero(root, data, code) {
    var hero = cloneTemplate(root, 'hero');
    if (!hero) return null;

    var info  = (data.precinto && data.precinto.informacion) || {};
    var region = data.industria && data.industria.comunidadAutonoma;
    var color  = data.color;

    if (color) hero.setAttribute('data-color', color);

    setText(hero.querySelector('[data-hero-title]'), info.titulo || '');
    setText(
      hero.querySelector('[data-hero-subtitle]'),
      'Precinto: ' + ((data.precinto && data.precinto.descripcion) || code)
    );
    setText(hero.querySelector('[data-hero-text]'), info.descripcion || '');
    setImg(hero.querySelector('[data-hero-img]'), info.imagen, info.titulo || 'Precinto');

    var chipsHost = hero.querySelector('[data-hero-chips]');
    if (chipsHost) {
      var chipValues = [];
      if (color)  chipValues.push(color);
      if (region) chipValues.push('📍 ' + region);
      chipValues.forEach(function (label) {
        var chip = cloneTemplate(root, 'chip');
        if (chip) {
          setText(chip, label);
          chipsHost.appendChild(chip);
        }
      });
    }

    return hero;
  }

  function buildWideCard(root, node) {
    if (!hasInfo(node)) return null;
    var card = cloneTemplate(root, 'card-wide');
    if (!card) return null;
    var info = node.informacion;
    setText(card.querySelector('[data-card-title]'), info.titulo);
    setText(card.querySelector('[data-card-text]'),  info.descripcion || '');
    setImg(card.querySelector('[data-card-img]'),    info.imagen, info.titulo);
    return card;
  }

  function buildGridCard(root, node) {
    if (!hasInfo(node)) return null;
    var card = cloneTemplate(root, 'card-grid');
    if (!card) return null;
    var info = node.informacion;
    setText(card.querySelector('[data-card-title]'), info.titulo);
    setText(card.querySelector('[data-card-text]'),  info.descripcion || '');
    setImg(card.querySelector('[data-card-img]'),    info.imagen, info.titulo);
    return card;
  }

  function buildAnadaCard(root, node) {
    if (!hasInfo(node)) return null;
    var card = cloneTemplate(root, 'card-anada');
    if (!card) return null;
    var info = node.informacion;
    setText(card.querySelector('[data-card-title]'), info.titulo);
    setText(
      card.querySelector('[data-card-date]'),
      node.descripcion ? 'Fecha: ' + node.descripcion : ''
    );
    setText(card.querySelector('[data-card-text]'),  info.descripcion || '');
    setImg(card.querySelector('[data-card-img]'),    info.imagen, info.titulo);
    return card;
  }

  function buildOrigenCard(root, region, copyTemplate) {
    if (!region) return null;
    var card = cloneTemplate(root, 'card-origen');
    if (!card) return null;
    setText(card.querySelector('[data-card-title]'),  'Origen del Producto');
    setText(card.querySelector('[data-card-region]'), region);
    setText(
      card.querySelector('[data-card-text]'),
      (copyTemplate || '').replace(/\{region\}/g, region)
    );
    return card;
  }

  function buildGrid(root, children) {
    var filled = children.filter(Boolean);
    if (filled.length === 0) return null;
    var grid = cloneTemplate(root, 'grid');
    if (!grid) return null;
    filled.forEach(function (child) { grid.appendChild(child); });
    return grid;
  }

  function renderSuccess(root, cfg, data, code) {
    var body = root.querySelector('[data-success-body]');
    if (!body) return;

    body.innerHTML = ''; // start fresh — the markup we append is built from <template>s, never from API HTML

    var lote = data.lote || {};

    var pieces = [
      buildHero(root, data, code),
      buildWideCard(root, data.tipo),
      buildGrid(root, [
        buildGridCard(root, lote.raza),
        buildGridCard(root, lote.alimentacion),
      ]),
      buildGrid(root, [
        buildAnadaCard(root, lote.fechaSacrificio),
        buildOrigenCard(root,
          data.industria && data.industria.comunidadAutonoma,
          cfg.copyOrigen),
      ]),
    ];

    pieces.forEach(function (node, i) {
      if (!node) return;
      // Soft stagger so cards fade in one after the other.
      node.style.animationDelay = (i * 70) + 'ms';
      body.appendChild(node);
    });

    setState(root, 'success');
  }

  // ------------ network ------------

  function fetchSeal(cfg, code) {
    return fetch(cfg.endpoint, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'apikey':        cfg.anonKey,
        'Authorization': 'Bearer ' + cfg.anonKey,
        'Content-Type':  'application/json',
        'Accept':        'application/json',
      },
      body: JSON.stringify({ numero: code }),
    }).then(function (r) {
      return r.json().catch(function () {
        return { error: 'Respuesta no válida del servidor.' };
      });
    });
  }

  // ------------ entry point ------------

  function readConfig(root) {
    var cfg = {};
    try { cfg = JSON.parse(root.dataset.config || '{}'); }
    catch (e) { cfg = {}; }
    cfg.queryParam   = cfg.queryParam   || 'cod';
    cfg.copyOrigen   = cfg.copyOrigen   || '';
    cfg.headingEmpty = cfg.headingEmpty || 'Introduce el código del precinto';
    return cfg;
  }

  function init(root) {
    var cfg = readConfig(root);
    var code = (new URLSearchParams(window.location.search).get(cfg.queryParam) || '').trim();

    if (!code) {
      renderEmpty(root, cfg);
      return;
    }

    setState(root, 'loading');

    if (!cfg.endpoint || !cfg.anonKey) {
      renderError(
        root,
        'Configuración incompleta',
        'Falta el endpoint o la clave de Supabase en la configuración.',
        { back: true }
      );
      return;
    }

    fetchSeal(cfg, code).then(function (data) {
      if (data && data.error) {
        renderError(root, data.error, data.subError || '', { back: true });
        return;
      }
      if (!data || !data.precinto || !data.precinto.informacion || !data.precinto.informacion.titulo) {
        // Defensive: the API responded but with no meaningful payload.
        renderError(
          root,
          'No se han encontrado datos para este precinto.',
          'Comprueba el código e inténtalo de nuevo.',
          { back: true }
        );
        return;
      }
      renderSuccess(root, cfg, data, code);
    }).catch(function () {
      renderError(
        root,
        'Error de conexión',
        'No se ha podido conectar con el servidor. Comprueba tu conexión e inténtalo de nuevo.',
        { retry: true, back: true }
      );
    });
  }

  function bootAll(scope) {
    (scope || document).querySelectorAll(SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bootAll(document); });
  } else {
    bootAll(document);
  }

  // Shopify theme editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    if (e && e.target) bootAll(e.target);
  });
})();